import { config } from "@noted/configuration";
import { NcGlobalProvider } from "@noted/noted-components";
import { Suspense, useEffect, useMemo } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { configureFetchers } from "~/api/fetchers";
import { FeaturePermission } from "~/api/graphql-types";
import Dashboard from "~/app/client-records/dashboard";
import Logout from "~/app/logout";
import { UserSettings } from "~/app/user-settings";
import { AccountState, useAccount } from "~/components/account-context/account.context";
import { useAuth } from "~/components/auth-provider";
import { useLogoutTracker } from "~/components/auth-provider/use-logout-tracker";
import AuthTimeoutHandler from "~/components/auth-timeout/auth-timeout-handler";
import { AuthTimeoutModal } from "~/components/auth-timeout/auth-timeout-modal";
import UserInteractionDetection from "~/components/auth-timeout/user-interaction-detection";
import { FeatureState, useFeatures } from "~/components/features.context";
import { Intercom } from "~/components/intercom";
import Header from "~/components/layout/header";
import Navigation from "~/components/layout/navigation";
import LegacyPrintView from "~/components/legacy-print-view";
import { Shell } from "~/components/shell";
import { useI18n } from "~/hooks/use-i18n";
import { VersionUpdateController } from "~/hooks/version-update-controller";
import { retryLazy } from "~/utilities/retry-lazy";

import Banners from "../components/banners/banners";
import ErrorBoundary from "../components/error-boundary";
import UpgradeModal from "./administration/subscription/upgrade-modal";
import AnonymousErrorPage from "./anonymous-error-page";
import { PrivateRoute } from "./private-route";
import { OnboardingState } from "./scheduling/agenda/use-agenda-onboarding";
import Verify from "./verify";
import { LoadingPage } from "./waiting-component";

const Administration = retryLazy(() => import("./administration/administration-routes"));
const Scheduling = retryLazy(() => import("./scheduling/scheduling-routes"));
const Primhd = retryLazy(() => import("./primhd"));
const Auditing = retryLazy(() => import("./auditing/auditing-routes"));
const Extracts = retryLazy(() => import("./reporting/extract"));
const RawRoutes = retryLazy(() => import("./raw/raw-routes"));
const OrgRoutes = retryLazy(() => import("~/app/client-records/org-routes"));
const AsfDemoPage = retryLazy(() => import("./asf-demo"));

const AdminProtectedRoute = () => {
  const { account } = useAccount();
  const { isAdminUser } = account;
  if (!isAdminUser) return null;
  return <Outlet />;
};

const SchedulingProtectedRoute = () => {
  const { featureEnabled } = useFeatures();
  const { canSchedule } = useAccount();
  if (!featureEnabled(FeaturePermission.BookingSystem) || !canSchedule) return null;
  return <Outlet />;
};

const FeatureProtectedRoute = ({ permission }: { permission: FeaturePermission }) => {
  const { featureEnabled } = useFeatures();
  const enabled = featureEnabled(permission);
  if (!enabled) return null;
  return <Outlet />;
};

const App = () => {
  const { t, locale } = useI18n();
  const location = useLocation();
  const uiContext = useMemo(() => ({ locale, t }), []);

  const { isLoading, isAuthenticated, getToken, session, login } = useAuth();
  const logoutHasStarted = useLogoutTracker(state => state.logoutHasStarted);

  useEffect(() => {
    if (isLoading || isAuthenticated || logoutHasStarted) {
      return;
    }

    (async (): Promise<void> => {
      await login({
        state: {
          redirectTo: location.pathname + location.search,
        },
      });
    })();
  }, [isLoading, isAuthenticated, login, logoutHasStarted]);

  if (!isAuthenticated || isLoading) {
    return null;
  }

  configureFetchers(getToken, session);

  return (
    <NcGlobalProvider value={uiContext}>
      <ErrorBoundary fallback={<AnonymousErrorPage />}>
        <Suspense fallback={<LoadingPage />}>
          <VersionUpdateController />
          <Routes>
            <Route path="/logout" element={<Logout />} />
            <Route path="/verify-account" element={<Verify />} />
            <Route path="/something-went-wrong" element={<AnonymousErrorPage />} />

            <Route
              path="/*"
              element={
                <AccountState>
                  <PrivateRoute>
                    <FeatureState>
                      <OnboardingState>
                        <Outlet />
                      </OnboardingState>
                    </FeatureState>
                  </PrivateRoute>
                  <Intercom />
                </AccountState>
              }
            >
              <Route path="raw/*" element={<RawRoutes />} />

              <Route path="n/print-client/*" element={<LegacyPrintView />} />
              <Route path="n/print-records/*" element={<LegacyPrintView />} />
              <Route path="n/print-generic-tab/*" element={<LegacyPrintView />} />
              <Route path="n/letter-preview/*" element={<LegacyPrintView />} />

              <Route
                path="*"
                element={
                  <Shell>
                    <Banners />
                    <Header />
                    <div className="relative flex flex-1 flex-row overflow-auto">
                      <Navigation />
                      <Main>
                        <Suspense fallback={<LoadingPage />}>
                          <Outlet />

                          <AuthTimeoutHandler>
                            {({ state, renewSessionTime }) => (
                              <>
                                <AuthTimeoutModal
                                  state={state}
                                  renewSessionTime={renewSessionTime}
                                />
                                <UserInteractionDetection renewSessionTime={renewSessionTime} />
                              </>
                            )}
                          </AuthTimeoutHandler>
                        </Suspense>
                      </Main>
                      <UpgradeModal />
                    </div>
                  </Shell>
                }
              >
                <Route element={<AdminProtectedRoute />}>
                  <Route path="admin/*" element={<Administration />} />
                </Route>

                <Route path="dashboard" element={<Dashboard />} />

                <Route path="n/extract/*" element={<Extracts />} />

                <Route path="n/*" element={<OrgRoutes />} />

                <Route element={<SchedulingProtectedRoute />}>
                  <Route path="scheduling/*" element={<Scheduling />} />
                </Route>

                <Route element={<FeatureProtectedRoute permission={FeaturePermission.Primhd} />}>
                  <Route path="primhd/*" element={<Primhd />} />
                </Route>

                <Route
                  element={<FeatureProtectedRoute permission={FeaturePermission.AuditTrail} />}
                >
                  <Route path="auditing/*" element={<Auditing />} />
                </Route>

                <Route path="user-settings/*" element={<UserSettings />} />
                <Route path="asf" element={<AsfDemoPage />} />
              </Route>
            </Route>
            <Route path="/" element={<RerouteFallback />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </NcGlobalProvider>
  );
};

const Main = ({ ...props }) => (
  <main className="flex h-full w-full overflow-auto" id="main" {...props} />
);

const RerouteFallback = () => {
  if (window.localStorage.getItem("ngStorage-session")) {
    return <Navigate to="/dashboard" />;
  }
  window.location.href = config.landingUrl;
  return null;
};

export default App;
